import { useState, type FC } from 'react';
import { Button, FormHelperText, Input, Modal } from '@selfinspectionorg/ui-kit';
import type { KeyedMutator } from 'swr';
import type { IMfaPasskeyView, IMfaStatusView } from '@shared/services';
import styles from './mfa.module.scss';
import { formatDate, passkeyDeviceLabel, passkeyDisplayName } from './formatting';
import { usePasskeyManagement } from './usePasskeyManagement';
import { useStepUp } from './useStepUp';
import { StepUpModal } from './StepUpModal';
import PlusIcon from './assets/plus.svg?react';
import TrashIcon from './assets/trash.svg?react';
import { SectionHeading } from '@/modules/profile/components';

interface Props {
  passkeys: IMfaPasskeyView[];
  mutate: KeyedMutator<IMfaStatusView>;
}

/**
 * Passkey management as a stacked section of the Profile Security tab: lists registered passkeys, adds
 * one through the WebAuthn registration ceremony, and removes one after a confirmation step. Add/remove
 * revalidate the shared status cache so the list updates without a full reload. Behaviour is unchanged
 * from the retired Security-page card — only the presentation is a borderless stacked section.
 */
export const PasskeySection: FC<Props> = ({ passkeys, mutate }) => {
  const {
    requestPasskeyOptions,
    runCeremony,
    isAdding,
    confirmAddPasskey,
    cancelAddPasskey,
    isNamingPasskey,
    isSavingPasskey,
    addError,
    dismissAddError,
    removePasskey,
    removingId,
    removeError,
    dismissRemoveError,
  } = usePasskeyManagement(mutate);

  // Step-up gate in front of the ceremony: adding a passkey now requires the current password
  // (`addPasskeyOptions` → `MfaStepUpDto`), shared with the TOTP and email setup flows.
  const stepUp = useStepUp();
  const [isStepUpOpen, setIsStepUpOpen] = useState(false);

  // The passkey pending confirmation for removal; null when the confirm dialog is closed.
  const [pendingRemoval, setPendingRemoval] = useState<IMfaPasskeyView | null>(null);

  // The name being typed in the post-ceremony naming step.
  const [passkeyName, setPasskeyName] = useState('');

  const openStepUp = () => {
    stepUp.reset();
    setIsStepUpOpen(true);
  };

  const closeStepUp = () => {
    setIsStepUpOpen(false);
    stepUp.reset();
  };

  // Validate the password by fetching the WebAuthn options; a rejected password stays inline in the
  // prompt. On success close the prompt and run the ceremony from the returned options.
  const submitStepUp = async () => {
    const result = await stepUp.runStepUp(requestPasskeyOptions);

    if (result.ok) {
      setIsStepUpOpen(false);
      await runCeremony(result.value.optionsJSON);
    }
  };

  const closeNaming = () => {
    setPasskeyName('');
    cancelAddPasskey();
  };

  const saveNamedPasskey = async () => {
    const saved = await confirmAddPasskey(passkeyName);

    if (saved) {
      setPasskeyName('');
    }
  };

  const closeConfirm = () => {
    setPendingRemoval(null);
    dismissRemoveError();
  };

  const confirmRemoval = async () => {
    if (!pendingRemoval) {
      return;
    }

    // Close only on success; on failure the dialog stays open showing the error, item intact.
    const removed = await removePasskey(pendingRemoval.credentialId);

    if (removed) {
      setPendingRemoval(null);
    }
  };

  return (
    <section className={styles.section}>
      <SectionHeading title="Passkeys" description="Sign in with your device biometrics or a security key." />

      {passkeys.length === 0 ? (
        <p className={styles.empty} data-testid="passkeys-empty">
          You have no passkeys yet. Add one to sign in without a code.
        </p>
      ) : (
        <ul className={styles.list} data-testid="passkeys-list">
          {passkeys.map((passkey) => {
            const displayName = passkeyDisplayName(passkey);
            // When the user named the passkey, keep the device kind visible as secondary context.
            const showDeviceKind = displayName !== passkeyDeviceLabel(passkey.deviceType);

            return (
              <li key={passkey.credentialId} className={styles.item} data-testid="passkey-item">
                <div className={styles.itemInfo}>
                  <span className={styles.itemName}>{displayName}</span>
                  <span className={styles.itemMeta}>
                    {showDeviceKind && `${passkeyDeviceLabel(passkey.deviceType)} · `}
                    {passkey.backedUp ? 'Backed up' : 'Not backed up'} · Added {formatDate(passkey.createdAt)}
                  </span>
                  <span className={styles.itemMeta}>
                    {passkey.lastUsedAt ? `Last used ${formatDate(passkey.lastUsedAt)}` : 'Never used'}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => setPendingRemoval(passkey)}
                  disabled={removingId !== null}
                  aria-label={`Remove ${displayName}`}
                  data-testid="remove-passkey-button"
                >
                  <TrashIcon />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className={styles.actions}>
        <Button onClick={openStepUp} isLoading={isAdding} width="100%" data-testid="add-passkey-button">
          <span className={styles.buttonContent}>
            <PlusIcon />
            Add passkey
          </span>
        </Button>
        {/* Ceremony-phase errors only; naming-phase errors show inside the naming dialog. */}
        {addError && !isNamingPasskey && (
          <FormHelperText errored className={styles.error} data-testid="add-passkey-error">
            {addError.message}
          </FormHelperText>
        )}
      </div>

      <StepUpModal
        isOpen={isStepUpOpen}
        title="Confirm it's you"
        description="Enter your current password to add a passkey."
        password={stepUp.password}
        onPasswordChange={stepUp.setPassword}
        onSubmit={submitStepUp}
        onClose={closeStepUp}
        isVerifying={stepUp.isVerifying}
        error={stepUp.error}
      />

      <Modal.Wrapper isOpen={isNamingPasskey} onClose={closeNaming} id="namePasskeyModal" width="400px">
        <Modal.Title
          titleText="Name your passkey"
          additionalText="Give this passkey a name so you can recognize it later. You can leave it blank."
        />
        <Input
          name="passkeyName"
          label="Passkey name"
          placeholder="e.g. Work laptop"
          value={passkeyName}
          onChange={(event) => {
            setPasskeyName(event.target.value);
            dismissAddError();
          }}
          maxLength={64}
          autoFocus
          testId="passkey-name-input"
        />
        {addError && (
          <FormHelperText errored data-testid="name-passkey-error">
            {addError.message}
          </FormHelperText>
        )}
        <Modal.ActionButtons onCancelButtonClick={closeNaming}>
          <Button
            variant="primary"
            width="132px"
            onClick={saveNamedPasskey}
            isLoading={isSavingPasskey}
            data-testid="save-passkey-name-button"
          >
            Save
          </Button>
        </Modal.ActionButtons>
      </Modal.Wrapper>

      <Modal.Wrapper isOpen={pendingRemoval !== null} onClose={closeConfirm} id="removePasskeyModal" width="400px">
        <Modal.Title
          titleText="Remove this passkey?"
          additionalText="You will no longer be able to use it to sign in. This cannot be undone."
        />
        {removeError && (
          <FormHelperText errored data-testid="remove-passkey-error">
            {removeError}
          </FormHelperText>
        )}
        <Modal.ActionButtons onCancelButtonClick={closeConfirm}>
          <Button
            variant="danger"
            width="132px"
            onClick={confirmRemoval}
            isLoading={removingId !== null}
            data-testid="confirm-remove-passkey-button"
          >
            Remove
          </Button>
        </Modal.ActionButtons>
      </Modal.Wrapper>
    </section>
  );
};
